import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import type { DrugResolutionNode as DrugResolutionNodeType } from "@/lib/pharmacyFirst/types";
import type {
  ManagementPlanCapture,
  PreferenceCapture,
  EliminationLogEntry,
} from "@/lib/pharmacyFirst/types/audit.types";

export interface DrugResolutionAnswer {
  resolvedDrugId: string;
  rankedEligibleDrugs: string[];
  selectionReason?: string;
  /** Mandatory reason when the selected drug is in node.avoidRequiresReason. */
  overrideReason?: string;
  /** True when node.notSuitableOption was confirmed instead of a drug. */
  notSuitable?: boolean;
}

interface DrugResolutionNodeProps {
  node: DrugResolutionNodeType;
  eligibleDrugIds: string[];
  drugLabels: Record<string, string>;
  drugDescriptions?: Record<string, string>;
  accumulatedFlags?: string[];
  auditFlagLabels?: Record<string, string>;
  managementPlanCaptures?: ManagementPlanCapture[];
  preferenceCaptures?: PreferenceCapture[];
  isDarkMode?: boolean;
  stepNumber?: number;
  reinitiationDrugId?: string | null;
  /** Full starting eligible set, required by node.showEliminated. */
  initialEligibleDrugIds?: string[];
  /** Full elimination log, so every reason a drug was removed can be shown. */
  eliminationLog?: EliminationLogEntry[];
  onAnswer: (answer: DrugResolutionAnswer) => void;
}

// A hard stop (CONTRAINDICATED/EXCLUDE) must never read the same as a
// counselling point (MONITOR/COUNSEL), so each tag gets its own colour plus an
// on-screen legend.
const ACTION_TAG_LEGEND: { tag: string; description: string }[] = [
  { tag: "CONTRAINDICATED", description: "Must not be supplied" },
  { tag: "EXCLUDE", description: "Not available for this patient" },
  { tag: "AVOID", description: "Selectable only with a recorded reason" },
  { tag: "SPACE DOSES", description: "Dose times should be spaced apart" },
  { tag: "MONITOR", description: "Monitor after supply" },
  { tag: "CAUTION", description: "Check before supply" },
  { tag: "REFER", description: "Refer onward" },
  { tag: "COUNSEL", description: "Counselling point only — not a hard stop" },
];

function actionTagClasses(tag: string, isDarkMode: boolean): string {
  switch (tag) {
    case "CONTRAINDICATED":
    case "EXCLUDE":
      return isDarkMode ? "bg-red-950 text-red-300" : "bg-red-100 text-red-800";
    case "AVOID":
      return isDarkMode ? "bg-orange-950 text-orange-300" : "bg-orange-100 text-orange-800";
    case "SPACE DOSES":
      return isDarkMode ? "bg-purple-950 text-purple-300" : "bg-purple-100 text-purple-800";
    case "MONITOR":
      return isDarkMode ? "bg-blue-950 text-blue-300" : "bg-blue-100 text-blue-800";
    case "CAUTION":
      return isDarkMode ? "bg-amber-900 text-amber-200" : "bg-amber-100 text-amber-800";
    case "REFER":
      return isDarkMode ? "bg-yellow-950 text-yellow-300" : "bg-yellow-100 text-yellow-800";
    case "COUNSEL":
      return isDarkMode ? "bg-emerald-950 text-emerald-300" : "bg-emerald-100 text-emerald-800";
    default:
      return isDarkMode ? "bg-amber-900 text-amber-200" : "bg-amber-100 text-amber-800";
  }
}

const NOT_SUITABLE_ID = "__not_suitable__";

function applyRanking(
  eligibleDrugIds: string[],
  rankingOrder: string[]
): string[] {
  const ranked = rankingOrder.filter((id) => eligibleDrugIds.includes(id));
  const unranked = eligibleDrugIds.filter((id) => !rankingOrder.includes(id));
  return [...ranked, ...unranked];
}

export function DrugResolutionNode({
  node,
  eligibleDrugIds,
  drugLabels,
  drugDescriptions,
  accumulatedFlags = [],
  auditFlagLabels,
  managementPlanCaptures = [],
  preferenceCaptures = [],
  isDarkMode = false,
  stepNumber,
  reinitiationDrugId,
  initialEligibleDrugIds,
  eliminationLog = [],
  onAnswer,
}: DrugResolutionNodeProps) {
  const rankedEligible = applyRanking(eligibleDrugIds, node.rankingOrder);
  const recommendedDrugId = rankedEligible[0] ?? null;
  const alternatives = rankedEligible.slice(1);
  // Neutral (alphabetical) display order for the interactive selection list, so
  // a drug's position does not imply a system recommendation. The clinical
  // ranking is still used for recommendedDrugId and the emitted answer.
  const showEliminatedDrugIds =
    node.showEliminated && initialEligibleDrugIds ? initialEligibleDrugIds : eligibleDrugIds;
  const displayOrder = [...showEliminatedDrugIds].sort((a, b) =>
    (drugLabels[a] ?? a).localeCompare(drugLabels[b] ?? b)
  );
  const eliminationReasonsFor = (drugId: string): string[] =>
    eliminationLog.filter((entry) => entry.drugIds.includes(drugId)).map((entry) => entry.reason);

  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  // Quick-pick chips compose with free text into ONE stored reason.
  const [reasonPicks, setReasonPicks] = useState<string[]>([]);
  const [reasonFreeText, setReasonFreeText] = useState<string>(node.prefilledReason ?? "");
  const selectionReason = [...reasonPicks, reasonFreeText.trim()].filter(Boolean).join("; ");
  const toggleReasonPick = (pick: string) =>
    setReasonPicks((prev) =>
      prev.includes(pick) ? prev.filter((p) => p !== pick) : [...prev, pick]
    );
  const [overrideReason, setOverrideReason] = useState<string>("");

  // These arrive already resolved to their static form by
  // resolveDrugResolutionNode(); the function variant only exists in config.
  const resolvedAvoidRequiresReason = node.avoidRequiresReason as string[] | undefined;
  const resolvedSelectionTags = node.selectionTags as Record<string, string[]> | undefined;
  const resolvedReasonLines = node.reasonLines as Record<string, string[]> | undefined;
  const resolvedReasonBadges = node.reasonBadges as
    | Record<string, { label: string; title: string }[]>
    | undefined;
  const resolvedRowDetails = node.rowDetails as Record<string, string[]> | undefined;
  const resolvedRowCallouts = node.rowCallouts as Record<string, string[]> | undefined;
  const resolvedPrompt = node.selectionPrompt as string[] | undefined;
  const notSuitableSelected = selectedDrugId === NOT_SUITABLE_ID;

  const requiresOverrideReason =
    selectedDrugId !== null &&
    !notSuitableSelected &&
    (resolvedAvoidRequiresReason?.includes(selectedDrugId) ?? false);

  const usedTags = new Set<string>();
  if (resolvedSelectionTags) {
    for (const tagList of Object.values(resolvedSelectionTags)) {
      for (const tag of tagList) usedTags.add(tag);
    }
  }
  const actionTagLegendNode =
    usedTags.size > 0 ? (
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3 text-xs">
        {ACTION_TAG_LEGEND.filter((entry) => usedTags.has(entry.tag)).map((entry) => (
          <span key={entry.tag} className="inline-flex items-center gap-1">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${actionTagClasses(entry.tag, isDarkMode)}`}
            >
              {entry.tag}
            </span>
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              {node.tagLegend?.[entry.tag] ?? entry.description}
            </span>
          </span>
        ))}
      </div>
    ) : null;

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  if (recommendedDrugId === null) {
    return (
      <div className={cardClass}>
        {stepNumber !== undefined && (
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
            >
              {stepNumber}
            </div>
            <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Drug selection
            </h3>
          </div>
        )}
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No eligible drugs remain. This node should not have been reached —
            check that the pathway routes to the noDrugsRemaining outcome
            when the eligible set is empty.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const confirmDisabled =
    selectedDrugId === null ||
    selectionReason.trim() === "" ||
    (requiresOverrideReason && overrideReason.trim() === "");

  const handleConfirm = () => {
    if (!selectedDrugId || confirmDisabled) return;
    if (notSuitableSelected) {
      onAnswer({
        resolvedDrugId: "",
        rankedEligibleDrugs: rankedEligible,
        selectionReason,
        notSuitable: true,
      });
      return;
    }
    onAnswer({
      resolvedDrugId: selectedDrugId,
      rankedEligibleDrugs: rankedEligible,
      selectionReason,
      overrideReason: requiresOverrideReason ? overrideReason.trim() : undefined,
    });
  };

  const handleConfirmRecommended = () => {
    onAnswer({
      resolvedDrugId: recommendedDrugId,
      rankedEligibleDrugs: rankedEligible,
    });
  };

  return (
    <div className={cardClass}>
      {node.sectionLabel && (
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <p className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {node.sectionLabel}
          </p>
          {node.stepLabel && (
            <p
              data-testid="step-counter"
              className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              {node.stepLabel}
            </p>
          )}
        </div>
      )}
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && !node.sectionLabel && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
          >
            {stepNumber}
          </div>
        )}
        <div>
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.pharmacistOverrideAllowed ? "Select treatment" : "Drug selection"}
          </h3>
          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {node.pharmacistOverrideAllowed
              ? "Choose based on clinical eligibility and patient preference. Review any caution flags above before selecting."
              : "Select the treatment to supply"}
          </p>
        </div>
      </div>

      {node.displayAccumulatedFlags && accumulatedFlags.length > 0 && (
        <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-amber-700 bg-amber-950/30" : "border-amber-200 bg-amber-50"}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? "text-amber-200" : "text-amber-900"}`}>
            Accumulated caution flags
          </p>
          <ul className="space-y-1.5">
            {accumulatedFlags.map((flagId) => {
              const label = auditFlagLabels?.[flagId];
              return (
                <li key={flagId} className={`text-xs ${isDarkMode ? "text-amber-100" : "text-amber-900"}`}>
                  {label ?? flagId}
                  {label === undefined && (
                    <span className={`ml-1 ${isDarkMode ? "text-amber-400" : "text-amber-700"}`}>
                      (unlabelled flag)
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {node.displayAccumulatedManagementPlans && managementPlanCaptures.length > 0 && (
        <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-blue-700 bg-blue-950/30" : "border-blue-200 bg-blue-50"}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? "text-blue-200" : "text-blue-900"}`}>
            Accumulated management plans
          </p>
          <div className="space-y-2">
            {managementPlanCaptures.map((capture) => (
              <div
                key={`${capture.auditField}-${capture.capturedAt}`}
                className={`rounded-md border px-3 py-2 ${isDarkMode ? "border-blue-800 bg-blue-950/40" : "border-blue-100 bg-white"}`}
              >
                <p className={`text-xs font-medium mb-1 ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}>
                  {capture.displayLabel || capture.auditField || capture.nodeId}
                </p>
                <p className={`text-sm ${isDarkMode ? "text-blue-100" : "text-blue-950"}`}>
                  {capture.planText}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {node.displayAccumulatedPreferences && preferenceCaptures.length > 0 && (
        <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-indigo-700 bg-indigo-950/30" : "border-indigo-200 bg-indigo-50"}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? "text-indigo-200" : "text-indigo-900"}`}>
            Preferences noted
          </p>
          <div className="space-y-2">
            {preferenceCaptures.map((capture) => (
              <div
                key={`${capture.nodeId}-${capture.capturedAt}`}
                className={`rounded-md border px-3 py-2 ${isDarkMode ? "border-indigo-800 bg-indigo-950/40" : "border-indigo-100 bg-white"}`}
              >
                <p className={`text-xs font-medium mb-1 ${isDarkMode ? "text-indigo-300" : "text-indigo-800"}`}>
                  {capture.questionText}
                </p>
                <p className={`text-sm ${isDarkMode ? "text-indigo-100" : "text-indigo-950"}`}>
                  {capture.answerLabel}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {node.pharmacistOverrideAllowed ? (
        <>
          {resolvedPrompt && resolvedPrompt.length > 0 && (
            <div className="mb-3 space-y-0.5" data-testid="selection-prompt">
              {resolvedPrompt.map((line, i) => (
                <p
                  key={i}
                  className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  {line}
                </p>
              ))}
            </div>
          )}
          {actionTagLegendNode}
          <ul className="space-y-2 mb-4">
            {displayOrder.map((id) => {
              const isEliminated = !eligibleDrugIds.includes(id);
              // Caution tags (AVOID/MONITOR/...) are meaningless on an excluded row.
              const tags = (resolvedSelectionTags?.[id] ?? []).filter(
                (t) =>
                  !isEliminated || t === "EXCLUDE" || t === "CONTRAINDICATED" || t === "REFER"
              );
              return (
                <li key={id}>
                  <button
                    type="button"
                    disabled={isEliminated}
                    onClick={() => !isEliminated && setSelectedDrugId(id)}
                    className={`w-full text-left border rounded-lg p-4 transition-colors ${
                      isEliminated
                        ? isDarkMode
                          ? "border-gray-800 bg-gray-900 opacity-60 cursor-not-allowed"
                          : "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                        : selectedDrugId === id
                          ? isDarkMode
                            ? "border-blue-500 bg-blue-950/40 ring-1 ring-blue-500"
                            : "border-blue-400 bg-blue-50 ring-1 ring-blue-400"
                          : isDarkMode
                            ? "border-gray-700 bg-gray-800 hover:border-gray-500"
                            : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <span
                          className={`text-sm font-medium ${
                            isEliminated
                              ? isDarkMode ? "text-gray-500" : "text-gray-400"
                              : isDarkMode ? "text-gray-200" : "text-gray-800"
                          }`}
                        >
                          {drugLabels[id] ?? id}
                        </span>
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${actionTagClasses(tag, isDarkMode)}`}
                          >
                            {tag}
                          </span>
                        ))}
                        {reinitiationDrugId && id === reinitiationDrugId && (
                          <span
                            className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              isDarkMode ? "bg-blue-900 text-blue-200" : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            Re-initiation drug
                          </span>
                        )}
                        {drugDescriptions?.[id] && (
                          <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {drugDescriptions[id]}
                          </p>
                        )}
                        {!isEliminated &&
                          (resolvedRowCallouts?.[id] ?? []).map((line, i) => (
                            <div
                              key={i}
                              role="note"
                              data-testid={`row-callout-${id}`}
                              className={`mt-1 flex items-start gap-2 rounded border-2 border-amber-500 p-2 text-xs font-medium ${isDarkMode ? "bg-amber-950 text-amber-200" : "bg-amber-50 text-amber-900"}`}
                            >
                              <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                              <span>{line}</span>
                            </div>
                          ))}
                        {!isEliminated && (resolvedRowDetails?.[id]?.length ?? 0) > 0 && (
                          <ul className="mt-1 space-y-0.5" data-testid={`row-details-${id}`}>
                            {resolvedRowDetails![id].map((line, i) => (
                              <li
                                key={i}
                                className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                              >
                                {line}
                              </li>
                            ))}
                          </ul>
                        )}
                        {(resolvedReasonBadges?.[id] ?? []).map((b) => (
                          <span
                            key={b.label}
                            data-testid={`reason-badge-${id}`}
                            title={b.title}
                            className="mt-1 inline-block rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600"
                          >
                            {b.label}
                          </span>
                        ))}
                        {(resolvedReasonLines?.[id]?.length ?? 0) > 0 ? (
                          <ul className="mt-1 space-y-0.5" data-testid={`reason-lines-${id}`}>
                            {resolvedReasonLines![id].map((line, i) => (
                              <li
                                key={i}
                                className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                              >
                                {line}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          isEliminated &&
                          eliminationReasonsFor(id).length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {eliminationReasonsFor(id).map((reason, i) => (
                                <li
                                  key={i}
                                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  {reason}
                                </li>
                              ))}
                            </ul>
                          )
                        )}
                      </div>
                      {!isEliminated && (
                        <CheckCircle2 className={`h-4 w-4 flex-shrink-0 ${
                          selectedDrugId === id
                            ? isDarkMode ? "text-blue-400" : "text-blue-600"
                            : "text-transparent"
                        }`} />
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {node.notSuitableOption && (
            <button
              type="button"
              aria-pressed={notSuitableSelected}
              onClick={() => setSelectedDrugId(NOT_SUITABLE_ID)}
              className={`w-full text-left border rounded-lg p-3 mb-4 text-sm font-medium transition-colors ${
                notSuitableSelected
                  ? isDarkMode
                    ? "border-blue-500 bg-blue-950/40 ring-1 ring-blue-500"
                    : "border-blue-400 bg-blue-50 ring-1 ring-blue-400"
                  : isDarkMode
                    ? "border-gray-700 bg-gray-800 hover:border-gray-500"
                    : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              {node.notSuitableOption.label}
            </button>
          )}

          {selectedDrugId !== null && (
            <div className="mb-4">
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                Reason for selecting this treatment
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              {(node.reasonQuickPicks?.length ?? 0) > 0 && (
                <div
                  className="flex flex-wrap gap-1.5 mb-2"
                  role="group"
                  aria-label="Reason quick picks"
                >
                  {node.reasonQuickPicks!.map((pick) => (
                    <button
                      key={pick}
                      type="button"
                      aria-pressed={reasonPicks.includes(pick)}
                      onClick={() => toggleReasonPick(pick)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs ${
                        reasonPicks.includes(pick)
                          ? "border-blue-500 bg-blue-100 text-blue-900"
                          : isDarkMode
                            ? "border-gray-600 text-gray-300"
                            : "border-gray-300 text-gray-700"
                      }`}
                    >
                      {pick}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                rows={3}
                value={reasonFreeText}
                onChange={(e) => setReasonFreeText(e.target.value)}
                placeholder="e.g. patient preference, contraindication to another option…"
                className={`w-full rounded-md border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500"
                    : "border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-blue-400 focus:border-blue-400"
                }`}
              />
            </div>
          )}

          {requiresOverrideReason && (
            <div className="mb-4">
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}>
                This selection requires an override reason
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <textarea
                rows={2}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Record why this flagged option is being selected…"
                className={`w-full rounded-md border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 ${
                  isDarkMode
                    ? "border-amber-700 bg-gray-700 text-white placeholder-gray-500 focus:ring-amber-500 focus:border-amber-500"
                    : "border-amber-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-amber-400 focus:border-amber-400"
                }`}
              />
            </div>
          )}

          <FlowPrimaryButton
            label={"Confirm selection — " + ((notSuitableSelected ? node.notSuitableOption?.label : (drugLabels[selectedDrugId ?? ""] ?? selectedDrugId)) ?? "")}
            className="w-full"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            blockedReason={
              selectedDrugId === null
                ? "Select a treatment to continue."
                : selectionReason.trim() === ""
                  ? "Enter a reason for your selection to continue."
                  : "Enter an override reason to continue."
            }
          />
        </>
      ) : (
        <>
          <div className={`border rounded-lg p-4 mb-4 ${isDarkMode ? "border-emerald-700 bg-emerald-950/40" : "border-emerald-200 bg-emerald-50"}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className={`text-sm font-semibold ${isDarkMode ? "text-emerald-200" : "text-emerald-900"}`}>
                  {drugLabels[recommendedDrugId] ?? recommendedDrugId}
                </p>
                {drugDescriptions?.[recommendedDrugId] && (
                  <p className={`text-xs mt-1 ${isDarkMode ? "text-emerald-300" : "text-emerald-800"}`}>
                    {drugDescriptions[recommendedDrugId]}
                  </p>
                )}
              </div>
            </div>
          </div>

          {alternatives.length > 0 && (
            <div className="mb-4">
              <p className={`text-xs font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Other eligible options
              </p>
              <ul className="space-y-1.5">
                {alternatives.map((id) => (
                  <li
                    key={id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-750" : "border-gray-100 bg-gray-50"}`}
                  >
                    <CheckCircle2 className={`h-3.5 w-3.5 flex-shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                        {drugLabels[id] ?? id}
                      </span>
                      {drugDescriptions?.[id] && (
                        <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                          {drugDescriptions[id]}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <FlowPrimaryButton
            label={"Confirm — proceed with " + (drugLabels[recommendedDrugId] ?? recommendedDrugId)}
            className="w-full"
            onClick={handleConfirmRecommended}
          />
        </>
      )}
    </div>
  );
}
